import type { MouseEvent } from 'react'
import {
	ENTRANCE_FRAME,
	ENTRANCE_PROMISES,
	ENTRANCE_TITLE,
	ENTRANCE_TRUST,
	PRIVACY_PDF_URL,
	SUPPORT_PHONE_DISPLAY,
	SUPPORT_PHONE_TEL,
} from '../config'
import { BatteryIcon, CalendarIcon, MicIcon, PenIcon, PinIcon } from './icons'

interface Props {
	leaving: boolean
	phoneShown: boolean
	onStart: (withMic: boolean) => void
	onCall: (e: MouseEvent<HTMLAnchorElement>) => void
}

const PROMISE_ICON = { battery: BatteryIcon, calendar: CalendarIcon, pin: PinIcon }

/** The calm landing beat — the browser needs a user gesture before the mic
    and audio may start, so the live orb only materializes after this tap.
    What stands here is a still preview of it: the same face and idle halo, so
    the screen shows what the site *is* before anyone taps anything. Both paths
    into the conversation are peers — speaking is the default, „Inkább írok"
    starts the identical flow without ever asking for the microphone. */
export function Entrance({ leaving, phoneShown, onStart, onCall }: Props) {
	// The preview orb is decorative — honour the same reduced-motion contract
	// the halo animations already carry in CSS.
	const calm = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false

	return (
		<div className={`am-entrance${leaving ? ' leaving' : ''}`}>
			<div className="am-entrance-inner">
				<div className="am-entrance-orb" aria-hidden="true">
					<div className="am-halo">
						<div className="am-halo-fill" />
					</div>
					<div className="am-orb-face">
						<video src="/avatar.mp4" autoPlay={!calm} loop muted playsInline preload="metadata" />
					</div>
				</div>

				<h1 className="am-entrance-title">{ENTRANCE_TITLE}</h1>
				<p className="am-entrance-frame">{ENTRANCE_FRAME}</p>

				<ul className="am-entrance-promises">
					{ENTRANCE_PROMISES.map((p) => {
						const Icon = PROMISE_ICON[p.icon]
						return (
							<li key={p.icon}>
								<span className="am-promise-coin">
									<Icon />
								</span>
								{p.text}
							</li>
						)
					})}
				</ul>

				<div className="am-entrance-actions">
					<button type="button" className="am-entrance-start" onClick={() => onStart(true)}>
						<MicIcon size={18} />
						Beszélgetés indítása
					</button>
					<button type="button" className="am-entrance-write" onClick={() => onStart(false)}>
						<PenIcon />
						Inkább írok
					</button>
				</div>

				<p className="am-entrance-note">
					Az indításkor a böngésző mikrofon-engedélyt kér — bármikor leállíthatja.
				</p>

				{/* Second entry to the same Phone_Show → Phone_Call pair as the header
				    pill: one warm in-column line, and once the number is revealed it
				    is also readable here — a desktop visitor cannot dial a tel: link. */}
				<p className="am-entrance-call">
					{phoneShown ? (
						<>
							Ügyfélszolgálat:{' '}
							<a href={`tel:${SUPPORT_PHONE_TEL}`} onClick={onCall}>
								{SUPPORT_PHONE_DISPLAY}
							</a>
						</>
					) : (
						<>
							Inkább telefonálna?{' '}
							<a href="#" onClick={onCall}>
								Hívja az ügyfélszolgálatot
							</a>
						</>
					)}
				</p>

				<ul className="am-entrance-trust">
					{ENTRANCE_TRUST.map((t) => (
						<li key={t}>{t}</li>
					))}
				</ul>

				<a className="am-entrance-privacy" href={PRIVACY_PDF_URL} target="_blank" rel="noopener">
					Adatkezelési tájékoztató
				</a>
				<p className="am-entrance-cookie-note">
					A látogatások méréséhez egy saját, 6 hónapig érvényes analitikai sütit (asos_vid) használunk, amely véletlenszerű azonosítót tartalmaz, személyes adatot nem. Ez segít megkülönböztetni az új és a visszatérő látogatókat, és nem kerül továbbításra harmadik félnek.
				</p>
			</div>
		</div>
	)
}
